import { useRef, useState } from "react";
import { notification, ProdutosService } from "../../../../services";
import {
  formatPrice,
  responseErros,
  timeoutBuscaAutocomplete,
} from "../../../../utils";
import {
  Autocomplete,
  Button,
  CurrencyInput,
  DataGrid,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  TextFielDecimalNumbers,
  Typography,
} from "../../../../components";
import shortid from "shortid";
import { tabelaVendaItemValidator } from "../middlewares";

const TabelaVendaItens = ({
  produto,
  setProduto,
  produtos,
  setProdutos,
  tabelaVenda,
  setTabelaVenda,
  tabelaVendaEntity,
  resetForm,
  tabelaProdutos,
  setTabelaProdutos,
  handleAjusteChange,
  handleOperacaoChange,
  isProdutoEdit,
  setIsProdutoEdit,
  loading,
  setLoading,
  setOpen,
  inputErros,
}) => {
  const inputRef = useRef();
  const produtosService = new ProdutosService();
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);

  const produtosColunas = [
    {
      field: "codigo",
      headerName: "Código",
      flex: 120,
    },
    {
      field: "descricao",
      headerName: "Produto",
      flex: 500,
    },
    {
      field: "fator",
      headerName: "Fator (%)",
      flex: 120,
      valueGetter: (params) => {
        return params.row.operacao == "somar"
          ? params.value
          : `- ${params.value}`;
      },
    },
    {
      field: "precoPraticado",
      headerName: "Preço Praticado",
      flex: 120,
      headerAlign: "end",
      align: "end",
      valueGetter: (params) => formatPrice(params.value, false),
    },
    {
      field: "precoVenda",
      headerName: "Preço Atual",
      flex: 120,
      headerAlign: "end",
      align: "end",
      valueGetter: (params) => formatPrice(params.value, false),
    },
    {
      field: "quantidadeMinima",
      headerName: "Qtd.",
      flex: 120,
    },
  ];

  async function buscarProdutos(filtros) {
    setLoading(true);
    try {
      const filtrosComuns = {
        ...filtros,
        orderBy: "codigo",
        order: "asc",
        ativado: true,
        limite: 20,
      };
      const res = await produtosService.getAll(filtrosComuns);
      if (!res.isAxiosError) {
        setProdutos(
          res.data.rows.map((produto) => ({
            ...produto,
            ...(produto.informacao || {}),
            id: produto.id,
          }))
        );
      } else {
        responseErros(res);
      }
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      notification.error("Erro ao buscar produtos.");
    } finally {
      setLoading(false);
    }
  }

  const handleChangeAutocompleteProduto = (produtoEscolhido) => {
    document.getElementById("produtoId").blur();
    if (produtoEscolhido) {
      const { informacao, ...rest } = produtoEscolhido;
      const produtoResultante = { ...rest, ...informacao, id: rest.id };
      setProduto(produtoResultante);
      setTabelaVenda((prevState) => {
        const newState = {
          ...prevState,
          precoPraticado: produtoResultante?.precoVenda,
          quantidadeMinima: 1,
          fator: null,
          precoAtual: produtoResultante?.precoVenda,
        };
        tabelaVendaEntity.setHandleChangeInput(
          "precoPraticado",
          produtoResultante?.precoVenda
        );
        tabelaVendaEntity.setHandleChangeInput("quantidadeMinima", 1);
        tabelaVendaEntity.setHandleChangeInput("fator", null);
        tabelaVendaEntity.setHandleChangeInput(
          "precoAtual",
          produtoResultante?.precoVenda
        );
        return newState;
      });
    } else {
      resetForm();
    }
  };

  const handleRemoverProduto = async (id) => {
    const data = tabelaProdutos.filter((item) => item.id !== id);
    const produtoSelecionado = tabelaProdutos.find(
      (item) => item.id === id
    ).descricao;
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir ${produtoSelecionado}?`
    );
    if (retornoAlerta.isConfirmed) {
      setTabelaProdutos(data);
    }
    resetForm();
  };

  const handleEditarProduto = async (id) => {
    const item = tabelaProdutos.find((item) => item.id === id);
    if (item) {
      setIsProdutoEdit(true);
    }
    setProduto({
      ...item,
      itemEditado: true,
    });
    setTabelaVenda((prevState) => {
      const newState = {
        ...prevState,
        fator: item?.fator,
        precoPraticado: item?.precoPraticado,
        quantidadeMinima: item?.quantidadeMinima,
      };
      tabelaVendaEntity.setHandleChangeInput("fator", item?.fator);
      tabelaVendaEntity.setHandleChangeInput(
        "precoPraticado",
        item?.precoPraticado
      );
      tabelaVendaEntity.setHandleChangeInput(
        "quantidadeMinima",
        item?.quantidadeMinima
      );
      return newState;
    });
  };

  const nextFocusInput = (event) => {
    let inputCount = 2;
    if (event.keyCode === 13 && event.target.nodeName === "INPUT") {
      const { form } = event.target;
      const index = Array.from(form.elements).indexOf(event.target);
      let element = form.elements[index + inputCount];
      if (element?.disabled) {
        element = form.elements[index + inputCount + 2];
      }
      if (element) {
        element.focus();
      }
      event.preventDefault();
    }
  };

  const onInputChangeProdutos = (event) => {
    const eventValue = event.target?.value.includes(",")
      ? event.target?.value.split(",")[0]
      : event.target?.value;
    if (
      event.key === "Backspace" &&
      inputRef.current === document.activeElement &&
      eventValue === ""
    ) {
      setProdutos([]);
    }
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const filtros = {
        codigo: eventValue,
        descricao: eventValue,
      };
      setLoadingAutoComplete(true);
      timeoutBuscaAutocomplete(buscarProdutos(filtros));
    }
  };

  const handleAdicionarProduto = (produto) => {
    const dadosValidacao = {
      produto: produto,
      fator: tabelaVenda.fator,
      quantidadeMinima: tabelaVenda.quantidadeMinima,
      precoPraticado: tabelaVenda.precoPraticado,
    };
    tabelaVendaItemValidator
      .validate(dadosValidacao, { abortEarly: false })
      .then(() => {
        if (produto?.itemEditado) {
          const produtoIndex = tabelaProdutos.findIndex(
            (obj) => obj.id == produto.id
          );
          const tabelaProdutosTemp = [
            ...tabelaProdutos.map((obj, index) => {
              if (index == produtoIndex) {
                const { quantidadeMinima, precoPraticado, fator, operacao } =
                  tabelaVenda;
                return {
                  ...obj,
                  ...produto,
                  estProdutoId: produto?.id,
                  quantidadeMinima,
                  precoPraticado,
                  fator,
                  operacao,
                };
              }
              return obj;
            }),
          ];
          setTabelaProdutos(tabelaProdutosTemp);
          tabelaVendaEntity.setChangeItens(tabelaProdutosTemp);
          setTabelaVenda({ ...tabelaVendaEntity });
        } else {
          const tabelaVendaProdutosTemp = [
            ...tabelaProdutos,
            {
              ...tabelaVenda,
              ...produto,
              precoAtual: produto?.precoVenda,
              estProdutoId: produto?.id,
              id: shortid.generate(),
            },
          ];
          tabelaVendaEntity.setChangeItens(tabelaVendaProdutosTemp);
          setTabelaVenda({ ...tabelaVendaEntity });
          setTabelaProdutos(tabelaVendaProdutosTemp);
        }
        inputErros.set(null);
        document.getElementById("produtoId").focus();
        resetForm();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  return (
    <>
      <form id="produtoForm">
        <Grid container spacing={2} alignItems="center" mb={2} columns={14}>
          <Grid item xs={4}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="produtoId"
                name="produtoId"
                options={produtos}
                autoHighlight
                loading={loadingAutoComplete}
                loadingText="Carregando"
                noOptionsText="Digite e pressione Enter"
                renderOption={(props, option) => {
                  let descricaoProduto = `${option?.codigo} - ${option?.descricao}`;
                  return option ? (
                    <li {...props}>
                      <div className="d-flex justify-content-between w-100">
                        <div>{descricaoProduto}</div>
                        <div>{option?.estoqueProduto?.estoque}</div>
                      </div>
                    </li>
                  ) : (
                    ""
                  );
                }}
                getOptionLabel={(option) => {
                  let descricaoProduto = `${option?.codigo} - ${option?.descricao}`;
                  if (option?.codigoBarra) {
                    descricaoProduto = `${option?.codigoBarra} - ${option?.codigo} - ${option?.descricao}`;
                  }
                  return option ? descricaoProduto : "";
                }}
                filterSelectedOptions
                onChange={(_, newValue) =>
                  handleChangeAutocompleteProduto(newValue)
                }
                value={produto}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant={"outlined"}
                    label="Buscar produto"
                    onKeyDown={(e) => onInputChangeProdutos(e)}
                    inputRef={inputRef}
                    error={inputErros.get("produto")}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={1}>
            <Typography variant="body2" gutterBottom>
              Preço Atual
            </Typography>
            <span style={{ fontWeight: 600 }}>
              {formatPrice(produto?.precoVenda || 0)}
            </span>
          </Grid>

          <Grid
            item
            xs={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 1,
            }}
          >
            <RadioGroup
              row
              name="operacao"
              value={tabelaVenda?.operacao}
              onChange={handleOperacaoChange}
              sx={{
                p: 0,
                m: 0,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <FormControlLabel
                value="somar"
                control={<Radio size="small" />}
                label="Somar"
                sx={{ m: 0, mr: 1 }}
              />
              <FormControlLabel
                value="subtrair"
                control={<Radio size="small" />}
                label="Subtrair"
                sx={{ m: 0 }}
              />
            </RadioGroup>
          </Grid>

          <Grid item xs={2}>
            <TextFielDecimalNumbers
              id="fator"
              name="fator"
              label="Fator"
              variant="outlined"
              required
              disabled={!produto}
              value={tabelaVenda?.fator}
              error={inputErros.get("fator")}
              onBlur={handleAjusteChange}
              onKeyDown={(e) => nextFocusInput(e)}
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <CurrencyInput
              id="precoPraticado"
              name="precoPraticado"
              label="Preço Praticado"
              required
              disabled={!produto}
              error={inputErros.get("precoPraticado")}
              onKeyDown={(e) => nextFocusInput(e)}
              value={tabelaVenda?.precoPraticado ?? ""}
              onBlur={handleAjusteChange}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              id="quantidadeMinima"
              name="quantidadeMinima"
              label="Quantidade Mínima"
              variant="outlined"
              margin="normal"
              type="number"
              required
              disabled={!produto}
              value={tabelaVenda?.quantidadeMinima}
              onChange={handleAjusteChange}
              error={inputErros.get("quantidadeMinima")}
              onKeyDown={(e) => nextFocusInput(e)}
              fullWidth
            />
          </Grid>
          <Grid item xs={1} sx={{ mt: 1 }}>
            {isProdutoEdit ? (
              <Button
                id="buttonAdicionar"
                variant="contained"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={() => handleAdicionarProduto(produto)}
              >
                <i
                  className="ph-bold ph-arrows-clockwise"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            ) : (
              <Button
                id="buttonAdicionar"
                variant="contained"
                color="success"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={() => handleAdicionarProduto(produto)}
              >
                <i
                  className="ph-bold ph-plus"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <Grid container spacing={2}>
        <Grid item xs={8} mb={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpen(true);
            }}
          >
            carregar produtos em lote
          </Button>
        </Grid>
      </Grid>
      <DataGrid
        rows={tabelaProdutos}
        columns={produtosColunas}
        rowCount={tabelaProdutos.length}
        loading={loading}
        noFilter
        deleteShow
        editShow
        onClickEdit={handleEditarProduto}
        onClickDelete={handleRemoverProduto}
      />
    </>
  );
};

export default TabelaVendaItens;
